/**
 * Setup for the palette's data-bound blocks (line items table, totals box, repeating section,
 * conditional section): pick the list and fields from the template's sample payload, or type
 * names, then insert the generated structure (blocks/bindingSetup.ts). Opened by the palette
 * before inserting — at the cursor or the remembered drop position — and by the block handle's
 * "Edit binding…", pre-filled by reading the existing structure back. Cancel changes nothing.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { Button, Modal } from '../ui'
import { useTemplateSamples } from '../../hooks/useTemplateSamples'
import { itemScalars, sampleLists, sampleScalars, type SampleScalar } from '../../blocks/sampleFields'
import {
  bindingJson, CONTENT_WIDTH_PT, currency, defaultAlign, defaultFormatters, fitWidths, FORMAT_CHOICES, formatKey, humanizeKey,
  LOOP_ALIAS, setShare, TOTALS_WIDTHS, type Align, type BindingSetup, type BlockSetup,
} from '../../blocks/bindingSetup'
import { applyBindingEdit, dropsElseContent } from '../../blocks/bindingEdit'
import { insertBlocks, setBlockSetupHandler, type SetupRequest } from '../../blocks/paletteInsert'
import { placeholderLabel } from '../../cdm/formatters'
import { placeholderNameError } from '../../cdm/placeholderEdit'
import { MAX_LOOP_SCAFFOLD_FIELDS } from '../../cdm/fieldInsert'
import type { FormatterSpec } from '../../cdm/types'

const TITLES: Record<BlockSetup, string> = {
  lineItems: 'Line items table', totals: 'Totals box', repeating: 'Repeating section', conditional: 'Conditional section',
}

interface FieldRow {
  id: number
  field: string
  label: string
  formatters: FormatterSpec[] | null
  align: Align
  /** Points while included (kept fitted to the content width); a placeholder weight otherwise. */
  width: number
  on: boolean
  total: boolean
}

interface Draft {
  list: string
  rows: FieldRow[]
  layout: 'paragraphs' | 'line'
  condField: string
  negate: boolean
  withElse: boolean
}

let nextRowId = 1

function row(field: string, patch: Partial<FieldRow> = {}): FieldRow {
  return { id: nextRowId++, field, label: humanizeKey(field), formatters: null, align: 'left', width: 1, on: true, total: false, ...patch }
}

/** Included rows' widths refitted to the content width, the others untouched. */
function refit(rows: FieldRow[], weights?: number[]): FieldRow[] {
  const on = rows.filter(r => r.on)
  const fitted = fitWidths(weights ?? on.map(r => r.width))
  let k = 0
  return rows.map(r => (r.on ? { ...r, width: fitted[k++] } : r))
}

function rowsFromScalars(kind: BlockSetup, scalars: SampleScalar[]): FieldRow[] {
  if (kind === 'totals') {
    const rows = scalars.map(s => row(s.path, {
      formatters: currency(), align: 'right', on: typeof s.value === 'number' && /subtotal|tax|total|discount|shipping/i.test(s.path),
    }))
    const total = rows.find(r => r.on && /(^|\.)total$/i.test(r.field)) ?? [...rows].reverse().find(r => r.on)
    return rows.map(r => (r === total ? { ...r, total: true } : r))
  }
  return refit(scalars.map((s, i) => {
    const formatters = defaultFormatters(s.path, s.value)
    return row(s.path, { formatters, align: defaultAlign(s.value, formatters), on: i < MAX_LOOP_SCAFFOLD_FIELDS })
  }))
}

function draftFrom(current: BindingSetup | null): Draft {
  const d: Draft = { list: '', rows: [], layout: 'paragraphs', condField: '', negate: false, withElse: false }
  if (!current) return d
  switch (current.kind) {
    case 'lineItems': return { ...d, list: current.list, rows: current.columns.map(c => row(c.field, { ...c })) }
    case 'repeating': return { ...d, list: current.list, layout: current.layout, rows: current.fields.map(f => row(f.field, { ...f })) }
    case 'totals': return { ...d, rows: current.rows.map(r => row(r.field, { ...r, align: 'right' })) }
    case 'conditional': return { ...d, condField: current.field, negate: current.negate, withElse: current.withElse }
  }
}

/** The setup the draft describes, whether or not it is complete enough to insert. */
function draftSetup(kind: BlockSetup, d: Draft): BindingSetup {
  const on = d.rows.filter(r => r.on)
  const base = (r: FieldRow) => ({ field: r.field, label: r.label, formatters: r.formatters })
  switch (kind) {
    case 'lineItems': return { kind, list: d.list.trim(), columns: on.map(r => ({ ...base(r), align: r.align, width: r.width })) }
    case 'repeating': return { kind, list: d.list.trim(), fields: on.map(base), layout: d.layout }
    case 'totals': return { kind, rows: on.map(r => ({ ...base(r), total: r.total })) }
    case 'conditional': return { kind, field: d.condField.trim(), negate: d.negate, withElse: d.withElse }
  }
}

function setupError(setup: BindingSetup): string | null {
  if (setup.kind === 'conditional') return placeholderNameError(setup.field) && 'Choose or type the field the section depends on.'
  if (setup.kind !== 'totals' && placeholderNameError(setup.list)) return 'Choose or type the list to repeat over.'
  const fields = setup.kind === 'lineItems' ? setup.columns : setup.kind === 'repeating' ? setup.fields : setup.rows
  if (!fields.length) return setup.kind === 'lineItems' ? 'Include at least one column.' : 'Include at least one field.'
  const bad = fields.find(f => placeholderNameError(setup.kind === 'totals' ? f.field : `${LOOP_ALIAS}.${f.field}`))
  return bad ? `“${bad.field}” is not a valid field name.` : null
}

export default function BindingSetupDialog({ editor, templateVersionId }: { editor: Editor | null; templateVersionId: string | null }) {
  const [request, setRequest] = useState<{ req: SetupRequest; n: number } | null>(null)

  useEffect(() => {
    if (!editor) return
    let n = 0
    setBlockSetupHandler(editor, req => setRequest({ req, n: ++n }))
    return () => setBlockSetupHandler(editor, null)
  }, [editor])

  if (!editor || !request) return null
  const close = () => { setRequest(null); editor.view.focus() }
  return <SetupBody key={request.n} editor={editor} req={request.req} templateVersionId={templateVersionId} onClose={close} />
}

function SetupBody({ editor, req, templateVersionId, onClose }: {
  editor: Editor; req: SetupRequest; templateVersionId: string | null; onClose: () => void
}) {
  const kind = req.kind
  const edit = 'edit' in req ? req.edit : null
  const { samples, sampleId, setSampleId, sampleData } = useTemplateSamples(templateVersionId)
  const [draft, setDraft] = useState<Draft>(() => draftFrom(edit?.current ?? null))
  const [typed, setTyped] = useState('')

  const lists = useMemo(() => sampleLists(sampleData), [sampleData])
  const scalars = useMemo(() => sampleScalars(sampleData), [sampleData])
  const listed = lists.find(l => l.path === draft.list.trim())
  const isLoop = kind === 'lineItems' || kind === 'repeating'

  // Seed from the sample once it loads: a first list or field when nothing is chosen yet, and
  // unticked candidates for the fields the current binding does not use.
  useEffect(() => {
    if (!sampleData) return
    setDraft(d => {
      if (kind === 'conditional') {
        if (d.condField) return d
        const pick = scalars.find(s => typeof s.value === 'boolean')
        return pick ? { ...d, condField: pick.path } : d
      }
      if (isLoop && !d.list && !d.rows.length && lists.length) {
        return { ...d, list: lists[0].path, rows: rowsFromScalars(kind, itemScalars(lists[0].items)) }
      }
      if (kind === 'totals' && !d.rows.length) return { ...d, rows: rowsFromScalars(kind, scalars) }
      const pool = isLoop ? itemScalars(lists.find(l => l.path === d.list.trim())?.items ?? []) : scalars
      const extra = rowsFromScalars(kind, pool.filter(s => !d.rows.some(r => r.field === s.path))).map(r => ({ ...r, on: false, total: false }))
      return extra.length ? { ...d, rows: [...d.rows, ...extra] } : d
    })
  }, [sampleData, lists, scalars, kind, isLoop])

  const setup = draftSetup(kind, draft)
  const error = setupError(setup)
  const losesElse = edit ? dropsElseContent(editor.state.doc, edit, setup) : false

  const patch = (p: Partial<Draft>) => setDraft(d => ({ ...d, ...p }))
  const updateRow = (id: number, p: Partial<FieldRow>) => setDraft(d => ({ ...d, rows: d.rows.map(r => (r.id === id ? { ...r, ...p } : r)) }))

  function pickList(path: string) {
    const l = lists.find(x => x.path === path)
    patch({ list: path, rows: l ? rowsFromScalars(kind, itemScalars(l.items)) : draft.rows })
  }

  function toggle(id: number, on: boolean) {
    setDraft(d => {
      const included = d.rows.filter(r => r.on)
      const avg = included.length ? included.reduce((a, r) => a + r.width, 0) / included.length : 1
      let rows = d.rows.map(r => (r.id === id ? { ...r, on, width: on ? avg : r.width, total: on ? r.total : false } : r))
      if (kind === 'totals' && on && !rows.some(r => r.total)) rows = rows.map(r => (r.id === id ? { ...r, total: true } : r))
      return { ...d, rows: kind === 'lineItems' ? refit(rows) : rows }
    })
  }

  function move(i: number, dir: -1 | 1) {
    setDraft(d => {
      const j = i + dir
      if (j < 0 || j >= d.rows.length) return d
      const rows = [...d.rows]
      ;[rows[i], rows[j]] = [rows[j], rows[i]]
      return { ...d, rows }
    })
  }

  function setWidthShare(id: number, pct: number) {
    setDraft(d => {
      const on = d.rows.filter(r => r.on)
      const i = on.findIndex(r => r.id === id)
      return i < 0 || !Number.isFinite(pct) ? d : { ...d, rows: refit(d.rows, setShare(on.map(r => r.width), i, pct)) }
    })
  }

  function addTyped() {
    let field = typed.trim()
    if (isLoop && field.startsWith(`${LOOP_ALIAS}.`)) field = field.slice(LOOP_ALIAS.length + 1)
    if (!field || draft.rows.some(r => r.field === field)) { setTyped(''); return }
    const avg = draft.rows.filter(r => r.on).reduce((a, r, _, all) => a + r.width / all.length, 0) || 1
    const added = row(field, kind === 'totals'
      ? { formatters: currency(), align: 'right', total: !draft.rows.some(r => r.on && r.total) }
      : { width: avg })
    patch({ rows: kind === 'lineItems' ? refit([...draft.rows, added]) : [...draft.rows, added] })
    setTyped('')
  }

  function confirm() {
    if (error) return
    if (edit) applyBindingEdit(editor, edit, setup)
    else insertBlocks(editor, bindingJson(setup), 'pos' in req ? req.pos : undefined)
    onClose()
  }

  const fitted = kind === 'lineItems' ? fitWidths(draft.rows.filter(r => r.on).map(r => r.width)) : []
  let onIndex = 0

  return (
    <Modal
      open
      onClose={onClose}
      width={760}
      title={edit ? `Edit binding: ${TITLES[kind]}` : `Set up ${TITLES[kind].toLowerCase()}`}
      subtitle={samples?.length ? 'Fields come from the template’s sample data; you can also type names.' : 'Type the field names your data will use.'}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={error !== null}>{edit ? 'Update' : 'Insert'}</Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3], fontSize: fontSize.sm, color: colors.text }}>
        {samples && samples.length > 1 && (
          <label style={labelStyle}>
            Sample
            <select aria-label="Sample" value={sampleId ?? ''} onChange={e => setSampleId(e.target.value)} style={input}>
              {samples.map(s => <option key={s.sampleId} value={s.sampleId}>{s.name}</option>)}
            </select>
          </label>
        )}

        {isLoop && (
          <Section title="List">
            {lists.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {lists.map(l => (
                  <Chip key={l.path} pressed={l.path === draft.list.trim()} label={`Use list ${l.path}`} onClick={() => pickList(l.path)}>
                    <span style={{ fontFamily: 'monospace' }}>{l.path}</span> <span style={{ color: colors.textMuted }}>· {l.items.length}</span>
                  </Chip>
                ))}
              </div>
            )}
            <input aria-label="List name" value={draft.list} placeholder="items" spellCheck={false}
              onChange={e => patch({ list: e.target.value })} style={{ ...input, fontFamily: 'monospace', width: 260 }} />
            {draft.list.trim() && !listed && samples?.length ? <Muted>Not in the sample — it will become a new loop.</Muted> : null}
          </Section>
        )}

        {kind === 'conditional' ? (
          <Section title="Condition">
            {scalars.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[...scalars].sort((a, b) => Number(typeof b.value === 'boolean') - Number(typeof a.value === 'boolean')).map(s => (
                  <Chip key={s.path} pressed={s.path === draft.condField.trim()} label={`Use field ${s.path}`} onClick={() => patch({ condField: s.path })}>
                    <span style={{ fontFamily: 'monospace' }}>{s.path}</span> <span style={{ color: colors.textMuted }}>{String(s.value)}</span>
                  </Chip>
                ))}
              </div>
            )}
            <input aria-label="Condition field" value={draft.condField} placeholder="showSection" spellCheck={false}
              onChange={e => patch({ condField: e.target.value })} style={{ ...input, fontFamily: 'monospace', width: 260 }} />
            <label style={checkLabel}><input type="checkbox" checked={draft.negate} onChange={e => patch({ negate: e.target.checked })} /> Show when false</label>
            <label style={checkLabel}><input type="checkbox" checked={draft.withElse} onChange={e => patch({ withElse: e.target.checked })} /> Add an else branch</label>
            {losesElse && <div role="alert" style={{ color: colors.warning, fontSize: fontSize.xs }}>Removing the else branch deletes its content.</div>}
          </Section>
        ) : (
          <Section title={kind === 'lineItems' ? 'Columns' : kind === 'totals' ? 'Rows' : 'Fields'}>
            {draft.rows.length === 0 && <Muted>{isLoop && !draft.list.trim() ? 'Choose a list first, or add field names below.' : 'Add field names below.'}</Muted>}
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {draft.rows.map((r, i) => {
                const share = r.on && kind === 'lineItems' ? Math.round((fitted[onIndex++] / CONTENT_WIDTH_PT) * 100) : null
                const key = formatKey(r.formatters)
                return (
                  <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: r.on ? 1 : 0.6 }}>
                    <input type="checkbox" aria-label={`Include ${r.field}`} checked={r.on} onChange={e => toggle(r.id, e.target.checked)} />
                    <span title={r.field} style={{ flex: '0 0 120px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.field}</span>
                    <input aria-label={`Label for ${r.field}`} value={r.label} onChange={e => updateRow(r.id, { label: e.target.value })} style={{ ...input, flex: '1 1 120px', width: 0 }} />
                    <select aria-label={`Format for ${r.field}`} value={key} style={{ ...input, flex: '0 0 140px' }}
                      onChange={e => {
                        const formatters = FORMAT_CHOICES.find(c => formatKey(c.formatters) === e.target.value)?.formatters ?? r.formatters
                        updateRow(r.id, kind === 'lineItems' && defaultAlign(undefined, formatters) === 'right' ? { formatters, align: 'right' } : { formatters })
                      }}>
                      {!FORMAT_CHOICES.some(c => formatKey(c.formatters) === key) && <option value={key}>{key}</option>}
                      {FORMAT_CHOICES.map(c => <option key={c.label} value={formatKey(c.formatters)}>{c.label}</option>)}
                    </select>
                    {kind === 'lineItems' && (
                      <>
                        <select aria-label={`Alignment for ${r.field}`} value={r.align} onChange={e => updateRow(r.id, { align: e.target.value as Align })} style={{ ...input, flex: '0 0 64px' }}>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </select>
                        <input type="number" aria-label={`Width % for ${r.field}`} min={5} max={95} disabled={share === null} value={share ?? ''}
                          onChange={e => r.on && setWidthShare(r.id, Number(e.target.value))} style={{ ...input, flex: '0 0 56px', width: 56 }} />
                      </>
                    )}
                    {kind === 'totals' && (
                      <label style={{ ...checkLabel, flex: '0 0 auto' }}>
                        <input type="radio" name="total-row" aria-label={`Total row ${r.field}`} checked={r.total} disabled={!r.on}
                          onChange={() => setDraft(d => ({ ...d, rows: d.rows.map(x => ({ ...x, total: x.id === r.id })) }))} /> Total
                      </label>
                    )}
                    <button type="button" aria-label={`Move ${r.field} up`} disabled={i === 0} onClick={() => move(i, -1)} style={iconBtn(i > 0)}>↑</button>
                    <button type="button" aria-label={`Move ${r.field} down`} disabled={i === draft.rows.length - 1} onClick={() => move(i, 1)} style={iconBtn(i < draft.rows.length - 1)}>↓</button>
                  </li>
                )
              })}
            </ol>
            <div style={{ display: 'flex', gap: 6 }}>
              <input aria-label="Field name" value={typed} placeholder={kind === 'totals' ? 'grandTotal' : 'fieldName'} spellCheck={false}
                onChange={e => setTyped(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTyped() } }}
                style={{ ...input, fontFamily: 'monospace', width: 200 }} />
              <Button variant="secondary" size="sm" onClick={addTyped} disabled={!typed.trim()}>Add field</Button>
            </div>
            {kind === 'repeating' && (
              <div style={{ display: 'flex', gap: spacing[3] }}>
                <label style={checkLabel}><input type="radio" name="layout" checked={draft.layout === 'paragraphs'} onChange={() => patch({ layout: 'paragraphs' })} /> One paragraph per field</label>
                <label style={checkLabel}><input type="radio" name="layout" checked={draft.layout === 'line'} onChange={() => patch({ layout: 'line' })} /> Single line</label>
              </div>
            )}
          </Section>
        )}

        <Section title="Preview">
          <BindingPreview setup={setup} />
        </Section>
        {error && <div role="status" style={{ color: colors.textSecondary, fontSize: fontSize.xs }}>{error}</div>}
      </div>
    </Modal>
  )
}

/** The generated structure as labels and {{placeholders}} — a sketch, not a render. */
export function BindingPreview({ setup }: { setup: BindingSetup }) {
  const cell: CSSProperties = { padding: '4px 6px', border: `1px dashed ${colors.border}`, fontSize: fontSize.xs, overflow: 'hidden', textOverflow: 'ellipsis' }
  const ph = (name: string, f: FormatterSpec[] | null) => <span style={{ fontFamily: 'monospace', color: '#4338ca' }}>{placeholderLabel(name, f)}</span>
  const loopTag = (list: string) => <div style={{ fontFamily: 'monospace', fontSize: fontSize.xs, color: colors.textSecondary }}>{`{{#each ${list || '…'}}}`}</div>
  const box: CSSProperties = { border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, background: colors.background }

  if (setup.kind === 'lineItems') {
    const widths = fitWidths(setup.columns.map(c => c.width))
    return (
      <div data-testid="binding-preview" style={box}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>{widths.map((w, i) => <col key={i} style={{ width: `${(w / CONTENT_WIDTH_PT) * 100}%` }} />)}</colgroup>
          <thead><tr>{setup.columns.map((c, i) => <th key={i} style={{ ...cell, background: '#F1F5F9', textAlign: c.align }}>{c.label}</th>)}</tr></thead>
        </table>
        {loopTag(setup.list)}
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>{widths.map((w, i) => <col key={i} style={{ width: `${(w / CONTENT_WIDTH_PT) * 100}%` }} />)}</colgroup>
          <tbody><tr>{setup.columns.map((c, i) => <td key={i} style={{ ...cell, textAlign: c.align }}>{ph(`${LOOP_ALIAS}.${c.field}`, c.formatters)}</td>)}</tr></tbody>
        </table>
      </div>
    )
  }
  if (setup.kind === 'repeating') {
    const part = (f: { field: string; label: string; formatters: FormatterSpec[] | null }) => <>{f.label ? `${f.label}: ` : ''}{ph(`${LOOP_ALIAS}.${f.field}`, f.formatters)}</>
    return (
      <div data-testid="binding-preview" style={box}>
        {loopTag(setup.list)}
        {setup.layout === 'line'
          ? <div>{setup.fields.map((f, i) => <span key={i}>{i > 0 && ' — '}{part(f)}</span>)}</div>
          : setup.fields.map((f, i) => <div key={i}>{part(f)}</div>)}
      </div>
    )
  }
  if (setup.kind === 'totals') {
    return (
      <div data-testid="binding-preview" style={box}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>{TOTALS_WIDTHS.map((w, i) => <col key={i} style={{ width: `${(w / CONTENT_WIDTH_PT) * 100}%` }} />)}</colgroup>
          <tbody>
            {setup.rows.map((r, i) => (
              <tr key={i} style={{ fontWeight: r.total ? 700 : 400 }}>
                <td style={{ ...cell, textAlign: 'right' }}>{r.label}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{ph(r.field, r.formatters)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  const tag = setup.negate ? 'unless' : 'if'
  return (
    <div data-testid="binding-preview" style={{ ...box, fontFamily: 'monospace', fontSize: fontSize.xs, color: colors.textSecondary }}>
      <div>{`{{#${tag} ${setup.field || '…'}}}`}</div>
      <div style={{ paddingLeft: 12, color: colors.textMuted }}>Content shown when {setup.field || '…'} is {setup.negate ? 'false' : 'true'}</div>
      {setup.withElse && <><div>{'{{else}}'}</div><div style={{ paddingLeft: 12, color: colors.textMuted }}>Content shown otherwise</div></>}
      <div>{'{{/if}}'}</div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
      {children}
    </section>
  )
}

function Chip({ pressed, label, onClick, children }: { pressed: boolean; label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={pressed} aria-label={label} onClick={onClick}
      style={{ padding: '3px 8px', border: `1px solid ${pressed ? colors.primary : colors.border}`, borderRadius: radius.full, background: pressed ? colors.primaryLight : colors.surface, color: colors.text, cursor: 'pointer', fontSize: fontSize.xs }}>
      {children}
    </button>
  )
}

const Muted = ({ children }: { children: ReactNode }) => <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>{children}</div>
const input: CSSProperties = { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, minWidth: 0, boxSizing: 'border-box', background: colors.surface }
const labelStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary, maxWidth: 260 }
const checkLabel: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, color: colors.textSecondary, cursor: 'pointer' }
const iconBtn = (enabled: boolean): CSSProperties => ({
  width: 24, height: 24, padding: 0, border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface,
  color: enabled ? colors.textSecondary : colors.textMuted, cursor: enabled ? 'pointer' : 'default', fontSize: fontSize.xs, flex: '0 0 auto',
})
